import React from "react";

/* The background of the empty page.
 *
 * Three layers of depth plus one of character, all behind the content:
 *
 *   a dot grid    texture, so the surface reads as paper rather than void
 *   three orbs    slow colour, breathing in brightness
 *   a white fade  dissolves both toward the edges
 *   RFP glyphs    line-drawn documents, checkmark, magnifier, plane, award,
 *                 pen — bobbing and tilting around the page edges
 *
 * The glyphs are the layer you actually SEE moving. The orbs alone failed at
 * that: a shape blurred to 70px has no edge for the eye to track, so even
 * 110px of travel barely registers. A sharp 40px glyph bobbing 20px is
 * unmissable — which is why the glyphs are crisp and the orbs stay soft.
 *
 * Placement keeps a clear channel through the middle of the page where the
 * greeting, cards and composer live; glyphs hug the left and right thirds.
 *
 * motion-reduce stops every animation outright. Colour and glyphs stay put;
 * only the movement goes.
 */

const STROKE = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

/* Line-art motifs of the RFP lifecycle: the document, the requirement met, the
 * discovery, the submission, the win, the signature. */
const ICONS = {
  document: (
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <polyline points="14 3 14 8 19 8" />
      <line x1="8.5" y1="12.5" x2="15.5" y2="12.5" />
      <line x1="8.5" y1="16" x2="13.5" y2="16" />
    </>
  ),
  check: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <polyline points="8.5 12.5 11 15 15.5 9.5" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <line x1="20" y1="20" x2="15.8" y2="15.8" />
    </>
  ),
  plane: (
    <>
      <path d="M21 3 3 11l7 2.5" />
      <path d="M21 3l-6.5 18-4.5-7.5" />
    </>
  ),
  award: (
    <>
      <circle cx="12" cy="9" r="5.5" />
      <path d="M8.8 13.5 7.5 21l4.5-2.6L16.5 21l-1.3-7.5" />
    </>
  ),
  pen: (
    <>
      <path d="M12 19.5h8.5" />
      <path d="M16.2 3.8a2 2 0 0 1 2.8 2.8L8.5 17.1 4 18.5l1.4-4.5z" />
    </>
  ),
};

/* Spread over both side-thirds, phases and delays all different so the group
 * never synchronises into a pattern. Sizes and opacities vary to fake depth —
 * bigger and darker reads nearer, smaller and fainter reads further away. */
const GLYPHS = [
  { icon: "document", pos: "left-[6%] top-[14%]",     size: 46, color: "text-[#A04A97]/30", float: "animate-copilot-float-a", delay: "0s" },
  { icon: "check",    pos: "right-[7%] top-[20%]",    size: 38, color: "text-[#C86B4F]/30", float: "animate-copilot-float-b", delay: "0.9s" },
  // The mid-page pair sits level with the suggestion cards, so it is positioned
  // off the CENTRE (cards are a centred 780px block), not off the pane edge —
  // percentage-of-pane positions grazed the cards on laptop widths. calc keeps
  // a fixed clearance outside the card column at any size, and below xl the
  // pair is dropped entirely: no room means no glyph, never a glyph under a card.
  { icon: "search",   pos: "hidden xl:block left-[calc(50%-478px)] top-[52%]",  size: 32, color: "text-[#8B3F83]/25", float: "animate-copilot-float-b", delay: "1.7s" },
  { icon: "plane",    pos: "hidden xl:block right-[calc(50%-486px)] top-[55%]", size: 42, color: "text-[#A04A97]/25", float: "animate-copilot-float-a", delay: "0.5s" },
  { icon: "award",    pos: "left-[8%] bottom-[16%]",  size: 40, color: "text-[#C86B4F]/25", float: "animate-copilot-float-b", delay: "2.3s" },
  { icon: "pen",      pos: "right-[6%] bottom-[20%]", size: 36, color: "text-[#8B3F83]/30", float: "animate-copilot-float-a", delay: "1.2s" },
];

const CopilotAmbient = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
    {/* Texture. Small, faint, and on a tight grid — at this scale it reads as
        paper rather than as dots. */}
    <div className="absolute inset-0 [background-image:radial-gradient(circle,rgba(24,16,28,0.16)_1px,transparent_1px)] [background-size:22px_22px] opacity-40" />

    {/* Colour and slow depth. Periods 14s / 18s / 23s so they never sync. */}
    <span className="absolute -left-[6%] top-[6%] block h-[440px] w-[440px] rounded-full bg-[radial-gradient(circle,rgba(160,74,151,0.34),transparent_68%)] blur-[70px] animate-copilot-drift-a motion-reduce:animate-none" />
    <span className="absolute -right-[4%] top-[2%] block h-[400px] w-[400px] rounded-full bg-[radial-gradient(circle,rgba(243,144,79,0.28),transparent_68%)] blur-[70px] animate-copilot-drift-b motion-reduce:animate-none" />
    <span className="absolute bottom-[-12%] left-[30%] block h-[480px] w-[480px] rounded-full bg-[radial-gradient(circle,rgba(139,63,131,0.26),transparent_70%)] blur-[80px] animate-copilot-drift-c motion-reduce:animate-none" />

    {/* Fades the grid and the orbs out toward the edges. A plain overlay rather
        than a CSS mask: masks still need vendor prefixes to be safe, and the
        page behind this is white anyway, so the result is identical. */}
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_68%_62%_at_50%_42%,transparent_0%,rgba(255,255,255,0.55)_62%,#fff_92%)]" />

    {/* The glyphs sit ABOVE the fade — sharp edges are the whole point, and
        letting the wash mute them would repeat the orbs' mistake. */}
    {GLYPHS.map((g) => (
      <span
        key={g.icon}
        className={`absolute ${g.pos} ${g.color} ${g.float} motion-reduce:animate-none`}
        style={{ animationDelay: g.delay }}
      >
        <svg aria-hidden="true" width={g.size} height={g.size} viewBox="0 0 24 24" {...STROKE}>
          {ICONS[g.icon]}
        </svg>
      </span>
    ))}
  </div>
);

export default CopilotAmbient;
